/**
 * useFlowPipeline -- encapsulates pipeline start/resume/subscribe/snapshot
 * logic and user chat messaging for the FlowProcessor screen.
 *
 * Ported from JSX (master:frontend/src/hooks/useFlowPipeline.js) to
 * TypeScript, adapted for the Python/FastAPI backend.
 *
 * Returns pipeline status, IPASS sections, LOC, chat messages, thinking
 * indicator, and action callbacks.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import {
  startFlowPipeline,
  resumeFlowPipeline,
  restoreFlowState,
  subscribeFlow,
  getFlowSnapshot,
  hasFlowPipeline,
  clearFlowPipeline,
  sendFlowUserMessage,
  getFlowSessionId,
} from '../services/pipelineManager'
import type { FlowPipelineSnapshot, PipelineResult } from '../services/pipelineManager'
import { parseIPASS, parseLOC, mergeIPASS, stripIPASSMarkers } from '../utils/ipassParser'
import type { IPASSSections, LOCData } from '../utils/ipassParser'

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export interface FlowChatMessage {
  role: 'user' | 'casey'
  text: string
  files?: string[]
  timestamp: number
}

interface UseFlowPipelineReturn {
  // Pipeline state
  snapshot: FlowPipelineSnapshot | null
  ipassSections: IPASSSections
  loc: LOCData | null
  isComplete: boolean
  isError: boolean
  elapsed: number
  thinkingMsg: string
  STEP_LABELS: Record<string, string>

  // Chat state
  chatMessages: FlowChatMessage[]
  userInput: string
  setUserInput: React.Dispatch<React.SetStateAction<string>>

  // Actions
  handleSendMessage: () => Promise<void>
  handleEnterWorkspace: (onComplete: (results: FlowWorkspaceResults) => void) => void
}

export interface FlowWorkspaceResults {
  casey: null
  ipassSections: IPASSSections
  loc: LOCData | null
  flowSessionId: string | null
  allStepResults: Record<string, PipelineResult>
  chatMessages: FlowChatMessage[]
}

// ────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────

const STEP_LABELS: Record<string, string> = {
  parse: 'Parsing Clinical Text',
  clinical: 'Analyzing Clinical Picture',
  loc: 'Determining Level of Care',
}

const THINKING_MESSAGES: Record<string, string[]> = {
  parse: [
    'Extracting chief complaint...',
    'Identifying active diagnoses...',
    'Structuring into IPASS format...',
    'Writing Illness Severity section...',
    'Composing Patient Summary...',
  ],
  clinical: [
    'Building action items...',
    'Planning contingencies...',
    'Identifying critical interventions...',
    'Evaluating situation awareness...',
  ],
  loc: [
    'Evaluating acuity level...',
    'Assessing disposition criteria...',
    'Determining telemetry needs...',
    'Finalizing signout synthesis...',
  ],
}

// ────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────

const useFlowPipeline = (
  workspaceId: string | null,
): UseFlowPipelineReturn => {
  const patientId = workspaceId || 'default'

  const [snapshot, setSnapshot] = useState<FlowPipelineSnapshot | null>(() => getFlowSnapshot(patientId))
  const [ipassSections, setIpassSections] = useState<IPASSSections>({})
  const [loc, setLoc] = useState<LOCData | null>(null)
  const [userInput, setUserInput] = useState('')
  const [chatMessages, setChatMessages] = useState<FlowChatMessage[]>(() => {
    try {
      const saved = sessionStorage.getItem(`casey-flow-chat-${patientId}`)
      return saved ? JSON.parse(saved) : []
    } catch (err) { console.warn('[useFlowPipeline] Failed to restore chat messages from sessionStorage:', err); return [] }
  })
  const [thinkingMsg, setThinkingMsg] = useState('')
  const thinkingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Persist chat messages
  useEffect(() => {
    try {
      sessionStorage.setItem(`casey-flow-chat-${patientId}`, JSON.stringify(chatMessages))
    } catch (err) { console.warn('[useFlowPipeline] Failed to persist chat messages to sessionStorage:', err) }
  }, [chatMessages, patientId])

  // Hydrate IPASS sections from step results
  const hydrateFromStepResults = useCallback((snap: FlowPipelineSnapshot) => {
    for (const stepId of ['parse', 'clinical', 'loc']) {
      const result = snap.stepResults[stepId]
      if (result?.output) {
        const sections = parseIPASS(result.output)
        if (Object.keys(sections).length > 0) {
          setIpassSections(prev => mergeIPASS(prev, sections))
        }
        const locData = parseLOC(result.output)
        if (locData) setLoc(locData)
      }
    }
  }, [])

  // Start or resume pipeline on mount
  useEffect(() => {
    if (!workspaceId) return

    if (!hasFlowPipeline(patientId)) {
      const saved = restoreFlowState(patientId)
      if (saved) {
        resumeFlowPipeline(patientId, workspaceId)
      } else {
        startFlowPipeline(patientId, workspaceId)
      }
    }

    const snap = getFlowSnapshot(patientId)
    if (snap) {
      setSnapshot(snap)
      hydrateFromStepResults(snap)
    }

    const unsub = subscribeFlow(patientId, (newSnap) => {
      setSnapshot(newSnap)
      hydrateFromStepResults(newSnap)
    })
    return unsub
  }, [patientId, workspaceId, hydrateFromStepResults])

  // Rotating thinking messages
  useEffect(() => {
    if (!snapshot) return
    const currentStepId = snapshot.steps[snapshot.currentStep]
    if (!currentStepId || snapshot.stepStatus[currentStepId] !== 'running') {
      setThinkingMsg('')
      if (thinkingIntervalRef.current) clearInterval(thinkingIntervalRef.current)
      return
    }
    const msgs = THINKING_MESSAGES[currentStepId] || ['Processing...']
    let idx = 0
    setThinkingMsg(msgs[0])
    thinkingIntervalRef.current = setInterval(() => {
      idx = (idx + 1) % msgs.length
      setThinkingMsg(msgs[idx])
    }, 4000)
    return () => {
      if (thinkingIntervalRef.current) clearInterval(thinkingIntervalRef.current)
    }
  }, [snapshot?.currentStep, snapshot?.stepStatus])

  const handleSendMessage = useCallback(async () => {
    const text = userInput.trim()
    if (!text || !workspaceId) return

    setUserInput('')
    setChatMessages(prev => [...prev, {
      role: 'user',
      text,
      timestamp: Date.now(),
    }])

    const result = await sendFlowUserMessage(patientId, workspaceId, text)
    if (result?.output) {
      const sections = parseIPASS(result.output)
      if (Object.keys(sections).length > 0) {
        setIpassSections(prev => mergeIPASS(prev, sections))
      }
      const locData = parseLOC(result.output)
      if (locData) setLoc(locData)

      const cleanOutput = stripIPASSMarkers(result.output)
      if (cleanOutput) {
        setChatMessages(prev => [...prev, { role: 'casey', text: cleanOutput, timestamp: Date.now() }])
      }
    }
  }, [userInput, patientId, workspaceId])

  const handleEnterWorkspace = useCallback((onComplete: (results: FlowWorkspaceResults) => void) => {
    if (!snapshot) return
    const results: FlowWorkspaceResults = {
      casey: null,
      ipassSections,
      loc,
      flowSessionId: getFlowSessionId(patientId),
      allStepResults: snapshot.stepResults,
      chatMessages,
    }
    clearFlowPipeline(patientId)
    onComplete(results)
  }, [snapshot, ipassSections, loc, patientId, chatMessages])

  const isComplete = snapshot?.status === 'complete'
  const isError = snapshot?.status === 'error'
  const elapsed = snapshot?.startTime ? Math.max(0, Math.floor((Date.now() - snapshot.startTime) / 1000)) : 0

  return {
    snapshot,
    ipassSections,
    loc,
    isComplete,
    isError,
    elapsed,
    thinkingMsg,
    STEP_LABELS,
    chatMessages,
    userInput,
    setUserInput,
    handleSendMessage,
    handleEnterWorkspace,
  }
}

export default useFlowPipeline
